import React from 'react';
import GridItem from '../../../../layout/GridComponents/GridItem';
import CustomTable from '../../../../tables/CustomTable';
import { CustomColumn } from '../../../../../types/components/tables/tableTypes';
import { percentageToNdecialPlaces } from '../../../../../utilities/numberFormatters';
import { Status } from '../../../../../types/redux/data/dataTypes';
import { mapStatusToColor } from '../../../../../utilities/colorUtilities';
import { Typography } from '@mui/material';
import type {
  MarketLookbackDateMetrics,
  MarketLookbackPayload,
} from '../../../../../api/rs-hg/risk/marketLookback.types';

interface MarketLookbackTableProps {
  data: MarketLookbackPayload;
  type: string;
}

interface MarketLookbackTableData {
  index: string;
  selectedDate: number;
  comparisonDate: number;
  percentageChange: number;
  limit: number;
  status: Status;
}

// this was just copied from another part of the code base
// I would would like to make more generic componets but it will require a lot of testing to make sure they work in all legacy cases
const StatusBox = ({ status, text }: { status: Status; text: string }) => (
  <div
    style={{
      padding: '0.50rem',
      borderRadius: 8,
      border: `1px solid ${mapStatusToColor(status)}`,
      backgroundColor: mapStatusToColor(status),
      // maxWidth: '25.00rem'
    }}
  >
    <Typography
      variant="h3"
      style={{
        transform: 'translateY(1px)',
        color: 'white',
        fontSize: '1.12rem',
      }}
    >
      {text}
    </Typography>
  </div>
);

const buildColumns = (
  type: string,
  comparisonDate: string,
  selectedDate: string,
): CustomColumn<MarketLookbackTableData>[] => {
  return [
    {
      title: 'Index',
      field: 'index',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title: `${type === 'vol' ? 'Volatility' : 'VaR'} (${comparisonDate})`,
      field: 'comparisonDate',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData: MarketLookbackTableData) =>
        percentageToNdecialPlaces(rowData.comparisonDate / 100, 2),
    },
    {
      title: `${type === 'vol' ? 'Volatility' : 'VaR'} (${selectedDate})`,
      field: 'selectedDate',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData: MarketLookbackTableData) =>
        percentageToNdecialPlaces(rowData.selectedDate / 100, 2),
    },
    {
      title: '% Change',
      field: 'percentageChange',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData: MarketLookbackTableData) =>
        percentageToNdecialPlaces(rowData.percentageChange, 2),
    },
    {
      title: 'Limit',
      field: 'limit',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData: MarketLookbackTableData) =>
        percentageToNdecialPlaces(rowData.limit / 100, 2),
    },
    {
      title: 'Status',
      field: 'status',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData: MarketLookbackTableData) => (
        <StatusBox status={rowData.status} text={rowData.status} />
      ),
    },
  ];
};

const getDateMetrics = (
  index: MarketLookbackPayload['market_info'][number],
  date: string,
): MarketLookbackDateMetrics | undefined => {
  const metrics = index[date];
  if (
    metrics &&
    typeof metrics === 'object' &&
    'index_vol' in metrics &&
    'index_var' in metrics
  ) {
    return metrics;
  }
  return undefined;
};

const buildTableData = (
  payload: MarketLookbackPayload,
  type: string,
): MarketLookbackTableData[] => {
  if (!payload.market_info?.length) {
    return [];
  }

  const selectedDate = payload.selected_date;
  const comparisonDate = payload.comparison_date;
  const metricKey = type === 'vol' ? 'index_vol' : 'index_var';
  const changeKey = `${type}_percentage_change` as
    | 'vol_percentage_change'
    | 'var_percentage_change';
  const statusKey = `${type}_status` as 'vol_status' | 'var_status';

  return payload.market_info.map((index) => {
    const selectedMetrics = getDateMetrics(index, selectedDate);
    const comparisonMetrics = getDateMetrics(index, comparisonDate);

    return {
      index: index.index_name,
      selectedDate: selectedMetrics?.[metricKey] ?? 0,
      comparisonDate: comparisonMetrics?.[metricKey] ?? 0,
      percentageChange: Number(index[changeKey] ?? 0),
      limit: index.limit,
      status: (index[statusKey] as Status) ?? Status.Pass,
    };
  });
};

const MarketLookbackTable: React.FC<MarketLookbackTableProps> = ({
  data,
  type,
}) => {
  const columns = buildColumns(
    type,
    data.comparison_date,
    data.selected_date,
  );
  const builtTableData = buildTableData(data, type);

  return (
    <GridItem xs={12} card>
      <CustomTable<MarketLookbackTableData>
        columns={columns}
        showToolbar
        data={builtTableData}
        title={`Market (${type === 'vol' ? 'Volatility' : 'VaR'})`}
        options={{
          paging: false,
          search: true,
          exportButton: true,
          draggable: false,
        }}
      />
    </GridItem>
  );
};

export default MarketLookbackTable;
